import { apiService, API_ENDPOINTS } from '@/shared/services/api';
import type { APIResponse } from '@/shared/types';
import type { VendorIntakeForm } from '@/features/campaigns';
import type { SalesReport, PayReport } from '@/features/agents';

export class FMOService {
  static async createVendorIntakeLink(data: Partial<VendorIntakeForm>): Promise<VendorIntakeForm> {
    const response = await apiService.post<APIResponse<VendorIntakeForm>>(
      API_ENDPOINTS.fmo.vendorIntakeLinks,
      data
    );
    return response.data.data!;
  }

  static async getVendorIntakeLinks(): Promise<VendorIntakeForm[]> {
    const response = await apiService.get<APIResponse<VendorIntakeForm[]>>(
      API_ENDPOINTS.fmo.vendorIntakeLinks
    );
    return response.data.data!;
  }

  static async updateVendorIntakeLink(id: string, data: Partial<VendorIntakeForm>): Promise<VendorIntakeForm> {
    const response = await apiService.put<APIResponse<VendorIntakeForm>>(
      `${API_ENDPOINTS.fmo.vendorIntakeLinks}/${id}`,
      data
    );
    return response.data.data!;
  }

  static async deleteVendorIntakeLink(id: string): Promise<void> {
    await apiService.delete(`${API_ENDPOINTS.fmo.vendorIntakeLinks}/${id}`);
  }

  static async assignFormLink(data: {
    organizationId: string;
    formId: string;
    percentageSplit: number;
  }): Promise<void> {
    await apiService.post<APIResponse>(
      API_ENDPOINTS.fmo.assignFormLink,
      data
    );
  }

  static async getFulfillmentLoadBalancer(organizationId?: string): Promise<any> {
    const params = organizationId ? `?organizationId=${organizationId}` : '';
    const response = await apiService.get<APIResponse<any>>(
      `${API_ENDPOINTS.fmo.fulfillmentLoadBalancer}${params}`
    );
    return response.data.data!;
  }

  static async getLoadBalancedLink(organizationId: string): Promise<{ url: string }> {
    const response = await apiService.get<APIResponse<{ url: string }>>(
      `${API_ENDPOINTS.fmo.fulfillmentLoadBalancer}/${organizationId}/link`
    );
    return response.data.data!;
  }

  static async getVendorIntakeAssignments(filters?: {
    organizationId?: string;
    formId?: string;
  }): Promise<{
    assignments: any[];
    totalAssignments: number;
    totalVendorIntakes: number;
    filters: any;
  }> {
    const params = new URLSearchParams();
    if (filters?.organizationId) params.append('organizationId', filters.organizationId);
    if (filters?.formId) params.append('formId', filters.formId);

    const response = await apiService.get<APIResponse<any>>(
      `${API_ENDPOINTS.fmo.vendorIntakeLinks.replace('/vendor-intake-links', '/vendor-intake')}/assignments?${params.toString()}`
    );
    return response.data.data!;
  }

  static async getOrganizations(): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(
      '/hr/organizations'
    );
    return response.data.data!;
  }

  static async getGlobalSalesReports(filters?: {
    emId?: string;
    dmId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<SalesReport[]> {
    const params = new URLSearchParams();
    if (filters?.emId) params.append('emId', filters.emId);
    if (filters?.dmId) params.append('dmId', filters.dmId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const response = await apiService.get<APIResponse<SalesReport[]>>(
      `${API_ENDPOINTS.fmo.globalSalesReports}?${params.toString()}`
    );
    return response.data.data!;
  }

  static async getGlobalPayReports(filters?: {
    emId?: string;
    dmId?: string;
    payPeriodStart?: string;
    payPeriodEnd?: string;
  }): Promise<PayReport[]> {
    const params = new URLSearchParams();
    if (filters?.emId) params.append('emId', filters.emId);
    if (filters?.dmId) params.append('dmId', filters.dmId);
    if (filters?.payPeriodStart) params.append('payPeriodStart', filters.payPeriodStart);
    if (filters?.payPeriodEnd) params.append('payPeriodEnd', filters.payPeriodEnd);

    const response = await apiService.get<APIResponse<PayReport[]>>(
      `${API_ENDPOINTS.fmo.globalPayReports}?${params.toString()}`
    );
    return response.data.data!;
  }
}
